import {
  Component,
  input,
  effect,
  signal,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
  Output,
  EventEmitter
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { QeqUser } from 'src/app/shared/interfaces/qeq.interface';
import {
  PetitionResponse,
  DriverResponse,
  DriverAssignment,
  DriverAssignmentResponse,
  OperativeRequestsResponse,
  OperativeRequestItem,
  PetitionRequest,
  OperativeDriversResponse,
  OperativaGroupRequestsResponse,
  OperativaGroupDriversResponse,
  ExerciseDriversResponse,
  ExerciseDriverRequestsResponse,
  VacationRequest,
  SimplifiedVacationRequest,
} from 'src/app/shared/interfaces/holidays.interface';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { VacationRequestDialogComponent } from '../vacation-request-dialog/vacation-request-dialog.component';

@Component({
  selector: 'app-petitions',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    ReactiveFormsModule,
    QeqPipe,
  ],
  templateUrl: './petitions.component.html',
  styleUrls: ['./petitions.component.css', '../../../styles/layout.css'],
})
export class PetitionsComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = [
    'employee_id',
    'name',
    'company',
    'age',
    'status',
    'vacation_days',
    'actions',
  ];

  year?: number;

  // input signals
  readonly onlyPetitions = input<boolean>();
  readonly operativeId = input<number>();
  readonly operativeGroupId = input<number>();
  readonly exerciseId = input<number>();
  readonly reloadFlag = input<boolean>();
  @Output() exportLoading = new EventEmitter<boolean>();

  // Signal to trigger data reload
  readonly reloadEffect = effect(() => {
    if (this.reloadFlag()) {
      const opId = this.operativeId();
      const groupId = this.operativeGroupId();
      const exId = this.exerciseId();
      // Prefer group ID if provided, otherwise use operative ID
      if (groupId && exId) {
        this.loadGroupData(groupId, exId);
      } else if (opId && exId) {
        this.loadData(opId, exId);
      } else if (exId){
        this.loadAllDriversData(exId);
      }
    }
  });

  // data from API - union type to support both petitions and drivers
  dataSource = new MatTableDataSource<
    PetitionResponse | DriverResponse | DriverAssignment
  >([]);

  // track expanded rows (only used when onlyPetitions() === false)
  readonly expanded = signal<Set<number>>(new Set());

  // Search filter
  filterControl = new FormControl('');

  // For managing subscriptions
  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly holidaysService = inject(HolidaysService);
  private readonly dialog = inject(MatDialog);

  constructor(
    private router: Router,
    private qeqService: QeqService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.year = this.route.snapshot.params['year'];
    const opId = this.operativeId();
    const groupId = this.operativeGroupId();
    const exId = this.exerciseId();
    // Prefer group ID if provided, otherwise use operative ID
    if (groupId && exId) {
      this.loadGroupData(groupId, exId);
    } else if (opId && exId) {
      this.loadData(opId, exId);
    }

    // Set up search filter
    this.setupFilter();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  loadData(operativeId: number, exerciseId: number): void {
    if (this.onlyPetitions()) {
      // Load petitions (employees with pending requests)
      this.holidaysService
        .getOperativeRequests({
          operative_id: operativeId,
          exercise_id: exerciseId,
          filter: 'pending',
        })
        .subscribe({
          next: (response: OperativeRequestsResponse) => {
            // Transform flat requests array into grouped PetitionResponse objects by employee
            this.dataSource.data = this.groupOperativeRequestsByEmployee(
              response.requests
            );
            // console.log('Petitions data:', this.dataSource.data);
          },
          error: (error) => {
            console.error('Error fetching petitions:', error);
          },
        });
    } else {
      // Load drivers using the new operative drivers endpoint
      this.holidaysService
        .getOperativeDrivers({
          operativa_id: operativeId,
          exercise_id: exerciseId,
        })
        .subscribe({
          next: (response: OperativeDriversResponse) => {
            this.dataSource.data = response.drivers;
            // console.log('Operative drivers data:', this.dataSource.data);
            // console.log('Operative info:', {
            //   id: response.operativa_id,
            //   name: response.operativa_name,
            //   exercise: response.exercise,
            // });
          },
          error: (error) => {
            console.error('Error fetching operative drivers:', error);
          },
        });
    }
  }

  loadGroupData(operativeGroupId: number, exerciseId: number): void {
    if (this.onlyPetitions()) {
      // Load petitions for the entire group
      this.holidaysService
        .getOperativaGroupRequests({
          operativa_group_id: operativeGroupId,
          exercise_id: exerciseId,
          filter: 'pending',
        })
        .subscribe({
          next: (response: OperativaGroupRequestsResponse) => {
            // Transform vacation requests into grouped PetitionResponse objects by employee
            this.dataSource.data = this.groupVacationRequestsByEmployee(
              response.requests
            );
            // console.log('Group petitions data:', this.dataSource.data);
            // console.log('Group info:', {
            //   id: response.operativa_group_id,
            //   name: response.operativa_group_name,
            //   total_requests: response.total_requests,
            // });
          },
          error: (error) => {
            console.error('Error fetching group petitions:', error);
          },
        });
    } else {
      // Load drivers for the entire group
      this.holidaysService
        .getOperativaGroupDrivers({
          operativa_group_id: operativeGroupId,
          exercise_id: exerciseId,
        })
        .subscribe({
          next: (response: OperativaGroupDriversResponse) => {
            this.dataSource.data = response.drivers;
            // console.log('Group drivers data:', this.dataSource.data);
            // console.log('Group info:', {
            //   id: response.operativa_group_id,
            //   name: response.operativa_group_name,
            //   total_drivers: response.total_drivers,
            // });
          },
          error: (error) => {
            console.error('Error fetching group drivers:', error);
          },
        });
    }
  }

  loadAllDriversData(exerciseId: number): void { 
    this.exportLoading.emit(true);
    if (this.onlyPetitions()) {
      // Load petitions for the entire group
      this.holidaysService
        .getExerciseDriversRequests(exerciseId, {
          exercise_id: exerciseId,
          filter: 'pending',
        })
        .subscribe({
          next: (response: ExerciseDriverRequestsResponse) => {
            this.exportLoading.emit(false);
            // Transform vacation requests into grouped PetitionResponse objects by employee
            this.dataSource.data = this.groupVacationRequestsByEmployee(
              response.requests
            );

          },
          error: (error) => {
            this.exportLoading.emit(false);
            console.error('Error fetching group petitions:', error);
          },
        });
    } else {
    
      this.holidaysService 
      .getAllDrivers(exerciseId, { 
        exercise_id: exerciseId, 
      }) 
      .subscribe({
        next: (response: ExerciseDriversResponse) => {
              this.exportLoading.emit(false);
              this.dataSource.data = response.drivers;;
            },
            error: (error) => {
              this.exportLoading.emit(false);
              console.error('Error fetching group drivers:', error);
            },
          }); 
    }
  }

  /**
   * Groups OperativeRequestItem[] (simplified format) by employee
   */
  private groupOperativeRequestsByEmployee(
    requests: OperativeRequestItem[]
  ): PetitionResponse[] {
    const employeeMap = new Map<number, PetitionResponse>();

    requests.forEach((request) => {
      const employeeId = request.employee.id;

      if (!employeeMap.has(employeeId)) {
        // Create new PetitionResponse for this employee
        employeeMap.set(employeeId, {
          id: employeeId,
          driver_id: request.driver_id,
          driver_assignment_id: request.driver_assignment_id,
          employee: request.employee,
          operativa_group: request.operativa_group,
          status_info: request.status_info || {
            employee_status: 'active',
            employee_status_color: 'green',
            has_pending_requests: true,
          },
          vacation_summary: {
            total_days_entitled: 0,
            total_days_used: 0,
            total_days_remaining: 0,
            days_pending_approval: 0,
            pending_to_take: '',
            personal_days_available: 0,
            personal_days_used: 0,
            personal_days_available_display: '',
          },
          requests: [],
        });
      }

      // Add this request to the employee's requests array
      const petitionResponse = employeeMap.get(employeeId)!;
      petitionResponse.requests.push({
        id: request.id,
        uuid: request.uuid,
        request_type: request.request_type,
        request_type_display: request.request_type_display,
        start_date: request.start_date,
        end_date: request.end_date,
        duration_days: request.duration_days,
        duration_weeks: request.duration_weeks,
        status: request.status,
        status_display: request.status_display,
        driver_id: request.driver_id,
        message: request.message,
        warning_message: '',
        can_approve: true,
        can_deny: true,
        toggle_enabled: true,
      });

      // Update days_pending_approval to sum all pending days
      petitionResponse.vacation_summary.days_pending_approval +=
        request.duration_days;
    });

    return Array.from(employeeMap.values());
  }

  /**
   * Groups simplified vacation requests by employee to create PetitionResponse structure
   * Handles SimplifiedVacationRequest format with pre-calculated dates and employee data
   */
  private groupVacationRequestsByEmployee(
    requests: SimplifiedVacationRequest[]
  ): PetitionResponse[] {
    const employeeMap = new Map<number, PetitionResponse>();

    requests.forEach((request) => {
      const employeeId = request.employee.id;
      const driverId = request.driver_id;
      const driver_assignment_id = request.driver_assignment;

      if (!employeeMap.has(employeeId)) {
        // Create new PetitionResponse for this employee
        employeeMap.set(employeeId, {
          id: employeeId,
          driver_id: driverId,
          driver_assignment_id: driver_assignment_id,
          employee: request.employee, // Employee data already included!
          operativa_group: request.operativa_group,
          status_info: request.status_info || {
            employee_status: 'active',
            employee_status_color: 'green',
            has_pending_requests: true,
          },
          vacation_summary: {
            total_days_entitled: 0,
            total_days_used: 0,
            total_days_remaining: 0,
            days_pending_approval: 0,
            pending_to_take: '',
            personal_days_available: 0,
            personal_days_used: 0,
            personal_days_available_display: '',
          },
          requests: [],
        });
      }

      // Add this request to the employee's requests array
      const petitionResponse = employeeMap.get(employeeId)!;
      petitionResponse.requests.push({
        id: request.id,
        uuid: request.uuid,
        request_type: request.request_type,
        request_type_display: request.request_type_display,
        start_date: request.start_date, // Already formatted!
        end_date: request.end_date, // Already formatted!
        duration_days: request.duration_days, // Already calculated!
        duration_weeks: request.duration_weeks, // Already calculated!
        status: request.status,
        status_display: request.status_display, // Already provided!
        driver_id: driverId,
        message: request.message, // Use the message from API
        warning_message: '',
        can_approve: true,
        can_deny: true,
        toggle_enabled: true,
        weeks: request.weeks, // Pass through weeks breakdown
      });

      // Update days_pending_approval to sum all pending days
      petitionResponse.vacation_summary.days_pending_approval +=
        request.duration_days;
    });

    return Array.from(employeeMap.values());
  }

  /**
   * Format Date from YYYY-MM-DD to DD/MM/YYYY
   */
  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  /**
   * Get status display text
   */
  private getStatusDisplay(status: string): string {
    const statusMap: { [key: string]: string } = {
      pending: 'Pending',
      approved: 'Aprobado',
      denied: 'Rechazado',
      rejected: 'Rechazado',
    };
    return statusMap[status] || status;
  }

  /**
   * Maps request status to a color for display
   */
  private getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'approved':
        return 'green';
      case 'denied':
        return 'red';
      default:
        return 'gray';
    }
  }

  expandedHas(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): boolean {
    if (this.isDriverAssignment(row)) {
      return this.expanded().has(row.driver);
    }
    return this.expanded().has(row.employee.id);
  }

  toggleRow(row: PetitionResponse | DriverResponse | DriverAssignment): void {
    if (this.onlyPetitions()) return; // no toggle in petitions-only mode
    const id = this.isDriverAssignment(row) ? row.driver : row.employee.id;
    this.expanded.update((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Type guard to check if a row is a DriverAssignment
  isDriverAssignment(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is DriverAssignment {
    return 'driver' in row && typeof row.driver === 'number';
  }

  // Type guard to check if a row is a PetitionResponse
  isPetitionResponse(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is PetitionResponse {
    return 'requests' in row;
  }

  // Type guard to check if a row is a DriverResponse
  // Check for status_info which is unique to DriverResponse
  isDriverResponse(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is DriverResponse {
    return 'status_info' in row && !('requests' in row);
  }

  onApproveRequest(uuid: string): void {
    if (!uuid) {
      console.error('UUID is required to approve request');
      return;
    }

    const dialogRef = this.dialog.open(VacationRequestDialogComponent, {
      data: {
        title: 'Aprobar petición',
        message: '¿Está seguro que desea aprobar esta petición de vacaciones?',
        confirmText: 'Aprobar',
        cancelText: 'Cancelar',
        toastSuccessMessage: 'Petición aprobada correctamente',
        toastErrorMessage: 'Error al aprobar la petición',
        handler: (comment?: string) =>
          this.holidaysService.acceptPetition(uuid),
        isAcceptAction: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Reload data after successful approval
        const opId = this.operativeId();
        const groupId = this.operativeGroupId();
        const exId = this.exerciseId();
        if (groupId && exId) {
          this.loadGroupData(groupId, exId);
        } else if (opId && exId) {
          this.loadData(opId, exId);
        } else if (exId){
          this.loadAllDriversData(exId);
        }
      }
    });
  }

  onDenyRequest(uuid: string): void {
    if (!uuid) {
      console.error('UUID is required to deny request');
      return;
    }

    const dialogRef = this.dialog.open(VacationRequestDialogComponent, {
      data: {
        title: 'Rechazar petición',
        message: '¿Está seguro que desea rechazar esta petición de vacaciones?',
        confirmText: 'Rechazar',
        cancelText: 'Cancelar',
        toastSuccessMessage: 'Petición rechazada correctamente',
        toastErrorMessage: 'Error al rechazar la petición',
        reject: true,
        handler: (comment?: string) =>
          this.holidaysService.rejectPetition(uuid, comment),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Reload data after successful denial
        const opId = this.operativeId();
        const groupId = this.operativeGroupId();
        const exId = this.exerciseId();
        if (groupId && exId) {
          this.loadGroupData(groupId, exId);
        } else if (opId && exId) {
          this.loadData(opId, exId);
        }
      }
    });
  }

  onDriverAssignmentClick(element: DriverAssignment): void {
    // console.log('DriverAssignment clicked!');
    // console.log('DriverAssignment element:', element);
    // console.log('element.driver:', element.driver);
    // console.log('All element keys:', Object.keys(element));
    // console.log('Full element:', JSON.stringify(element, null, 2));

    // For DriverAssignment, element.driver should be the driver ID
    // But it seems to be undefined, so we need to check what field to use
    if (!element.driver) {
      console.error('element.driver is undefined');
      return;
    }

    this.goToProfile(element.driver);
  }

  onDriverProfileClick(element: DriverResponse | PetitionResponse): void {
    // console.log('Driver/Petition clicked!');
    // console.log('Element:', element);
    // console.log('Driver ID field:', element.id);
    // console.log('All element keys:', Object.keys(element));

    // The driver assignment ID should be element.id
    // If that's undefined, log the entire object to debug
    if (!element.id) {
      console.error(
        'Driver ID is undefined. Full element:',
        JSON.stringify(element, null, 2)
      );
      return;
    }

    // Always use the current operativa group from the input (the group we're viewing)
    // not the driver's base operativa_group which may be different
    this.goToProfile(element.id, element.operativa_group?.id);
  }

  onPetitionProfileClick(element: PetitionResponse): void {
    // console.log('Driver/Petition clicked!');
    // console.log('Element:', element);
    // console.log('Driver ID field:', element.id);
    // console.log('All element keys:', Object.keys(element));

    // The driver assignment ID should be element.id
    // If that's undefined, log the entire object to debug
    if (!element.id) {
      console.error(
        'Driver ID is undefined. Full element:',
        JSON.stringify(element, null, 2)
      );
      return;
    }

    // Always use the current operativa group from the input (the group we're viewing)
    // not the driver's base operativa_group which may be different
    this.goToProfile(element.driver_assignment_id || element.driver_id, element.operativa_group?.id);
  }


  goToProfile(employeeId: number, operativaGroupId?: number): void {
    // Use the actual exercise and operativa group IDs from the inputs or parameters
    const exerciseId = this.exerciseId() || 1;
    const groupId = operativaGroupId || this.operativeGroupId() || 1;
    const year = this.year || new Date().getFullYear();

    // console.log('Navigating to profile:', {
    //   exerciseId,
    //   year,
    //   operativaGroupId: groupId,
    //   employeeId,
    // });

    // Validate all parameters
    if (!employeeId) {
      console.error('Employee ID is required but was undefined or null');
      return;
    }

    this.router.navigate([
      '/home',
      'vacations',
      'drivers',
      'exercise',
      exerciseId,
      year,
      'operational',
      groupId,
      'driver',
      employeeId,
    ]);
  }

  /**
   * Set up the search filter to listen for value changes
   */
  setupFilter(): void {
    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilter(value || '');
      });
  }

  /**
   * Apply filter to the data source
   * Searches by first_name (ID CONDUCTOR) and employee name (NOMBRE from QeQ data)
   */
  applyFilter(filterValue: string): void {
    filterValue = filterValue.trim().toLowerCase();

    this.dataSource.filterPredicate = (
      data: PetitionResponse | DriverResponse | DriverAssignment,
      filter: string
    ) => {
      let firstName = '';
      let company = '';

      // Extract first_name and company based on data type
      if (this.isDriverAssignment(data)) {
        // For DriverAssignment, use driver ID (convert to string)
        firstName = data.driver.toString().padStart(5, '0');
        company = ''; // DriverAssignment doesn't have company data
      } else {
        // For PetitionResponse and DriverResponse
        // first_name is the personnel number displayed in ID CONDUCTOR column
        firstName = data.employee.first_name?.toLowerCase() || '';
        company = data.employee.company?.toLowerCase() || '';

        // Get the QeQ name for this employee using full_name as the personnel number
        const qeqData = this.qeqService['getQeqFromStorage']?.();
        let qeqName = '';
        if (qeqData && qeqData.data && data.employee.full_name) {
          // full_name contains the personnel number (e.g., "00077" or "01676")
          const personnelNumber = data.employee.full_name.trim();
          const qeqUser = qeqData.data.find(
            (u: QeqUser) => u.personnelnumber === personnelNumber
          );
          qeqName = qeqUser ? qeqUser.name.toLowerCase() : '';
        }

        // Check if filter matches first_name (ID CONDUCTOR), QeQ name (NOMBRE), or company (EMPRESA)
        return (
          firstName.includes(filter) ||
          qeqName.includes(filter) ||
          company.includes(filter)
        );
      }

      // For DriverAssignment, check if filter matches driver ID only
      return firstName.includes(filter);
    };

    this.dataSource.filter = filterValue;

    // Reset paginator to first page when filtering
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
